import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronRight, Shield } from 'lucide-react';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);
  return <div className="min-h-screen flex flex-col bg-cream-100 font-sans text-gray-900" data-id="element-24">
      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-white/80 backdrop-blur-xl border-b border-forest-100 shadow-lg py-3' : 'bg-transparent py-6'}`} data-id="element-25">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26">
          <div className="flex justify-between items-center" data-id="element-27">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group" data-id="element-28">
              <div className="relative" data-id="element-29">
                <div className="absolute inset-0 bg-gradient-to-r from-forest-600 to-ember-500 rounded-2xl blur-md opacity-50 group-hover:opacity-75 transition-opacity" data-id="element-30"></div>
                <div className="relative w-12 h-12 bg-gradient-to-br from-forest-700 to-forest-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg transform group-hover:scale-105 transition-transform" data-id="element-31">
                  <Shield className="w-6 h-6" data-id="element-32" />
                </div>
              </div>
              <div data-id="element-33">
                <span className="text-xl font-display font-bold text-forest-900 tracking-tight block leading-none" data-id="element-34">
                  FoodSafe<span className="text-ember-500" data-id="element-35">Ireland</span>
                </span>
                <span className="text-[10px] text-forest-600 font-medium uppercase tracking-wider" data-id="element-36">
                  Training Excellence
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center space-x-1" data-id="element-37">
              <Link to="/" className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${location.pathname === '/' ? 'text-forest-700 bg-forest-50' : 'text-gray-700 hover:text-forest-700 hover:bg-forest-50/50'}`} data-id="element-38">
                Home
              </Link>
              <Link to="/courses" className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${location.pathname === '/courses' ? 'text-forest-700 bg-forest-50' : 'text-gray-700 hover:text-forest-700 hover:bg-forest-50/50'}`} data-id="element-39">
                Courses
              </Link>
              <Link to="/corporate" className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${location.pathname === '/corporate' ? 'text-forest-700 bg-forest-50' : 'text-gray-700 hover:text-forest-700 hover:bg-forest-50/50'}`} data-id="element-40">
                Corporate
              </Link>
              <Link to="/resources" className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${location.pathname === '/resources' ? 'text-forest-700 bg-forest-50' : 'text-gray-700 hover:text-forest-700 hover:bg-forest-50/50'}`} data-id="element-41">
                Resources
              </Link>
              <Link to="/help" className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${location.pathname === '/help' ? 'text-forest-700 bg-forest-50' : 'text-gray-700 hover:text-forest-700 hover:bg-forest-50/50'}`} data-id="element-42">
                Help
              </Link>
              <Link to="/blog" className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${location.pathname.startsWith('/blog') ? 'text-forest-700 bg-forest-50' : 'text-gray-700 hover:text-forest-700 hover:bg-forest-50/50'}`} data-id="element-43">
                Blog
              </Link>
            </nav>

            <div className="hidden lg:flex items-center space-x-3" data-id="element-44">
              <Link to="/contact" data-id="element-45">
                <Button size="sm" variant="outline" data-id="element-46">
                  Contact Us
                </Button>
              </Link>
              <Link to="/courses" data-id="element-47">
                <Button size="sm" variant="primary" data-id="element-48">
                  Book Training
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button className="lg:hidden p-2 text-forest-700 hover:bg-forest-50 rounded-xl transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-49">
              {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-50" /> : <Menu className="w-6 h-6" data-id="element-51" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence data-id="element-52">
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="lg:hidden bg-white border-t border-forest-100 shadow-2xl overflow-hidden" data-id="element-53">
              <div className="px-4 py-6 space-y-2" data-id="element-54">
                <Link to="/" className="block px-4 py-3 text-base font-semibold text-forest-900 hover:bg-forest-50 rounded-xl transition-colors" data-id="element-55">
                  Home
                </Link>
                <Link to="/courses" className="block px-4 py-3 text-base font-semibold text-forest-900 hover:bg-forest-50 rounded-xl transition-colors" data-id="element-56">
                  All Courses
                </Link>
                <Link to="/corporate" className="block px-4 py-3 text-base font-semibold text-forest-900 hover:bg-forest-50 rounded-xl transition-colors" data-id="element-57">
                  Corporate Services
                </Link>
                <Link to="/resources" className="block px-4 py-3 text-base font-semibold text-forest-900 hover:bg-forest-50 rounded-xl transition-colors" data-id="element-58">
                  Resources
                </Link>
                <Link to="/help" className="block px-4 py-3 text-base font-semibold text-forest-900 hover:bg-forest-50 rounded-xl transition-colors" data-id="element-59">
                  Help Centre
                </Link>
                <Link to="/blog" className="block px-4 py-3 text-base font-semibold text-forest-900 hover:bg-forest-50 rounded-xl transition-colors" data-id="element-60">
                  Blog
                </Link>
                <Link to="/contact" className="block px-4 py-3 text-base font-semibold text-forest-900 hover:bg-forest-50 rounded-xl transition-colors" data-id="element-61">
                  Contact Us
                </Link>
                <div className="pt-4 space-y-2" data-id="element-62">
                  <Link to="/courses" className="block w-full" data-id="element-63">
                    <Button fullWidth variant="primary" data-id="element-64">
                      Book Training
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-grow pt-20" data-id="element-65">{children}</main>

      {/* Footer */}
      <footer className="relative bg-gradient-to-br from-forest-900 via-forest-800 to-forest-900 text-white pt-20 pb-10 overflow-hidden" data-id="element-66">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-forest-600 via-ember-500 to-forest-600" data-id="element-67"></div>
        <div className="absolute top-10 right-10 w-64 h-64 bg-ember-500/10 rounded-full blur-3xl" data-id="element-68"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-forest-600/10 rounded-full blur-3xl" data-id="element-69"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-70">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16" data-id="element-71">
            <div className="space-y-6" data-id="element-72">
              <div className="flex items-center space-x-3" data-id="element-73">
                <div className="w-10 h-10 bg-gradient-to-br from-ember-500 to-ember-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-74">
                  <Shield className="w-5 h-5 text-white" data-id="element-75" />
                </div>
                <span className="text-xl font-display font-bold" data-id="element-76">
                  FoodSafe<span className="text-ember-400" data-id="element-77">Ireland</span>
                </span>
              </div>
              <p className="text-sm text-forest-200 leading-relaxed" data-id="element-78">
                Ireland's most innovative food safety training provider.
                Combining cutting-edge education with unmatched expertise.
              </p>
              <div className="flex space-x-3" data-id="element-79">
                <a href="#" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors" data-id="element-80">
                  <span className="text-xs font-bold" data-id="element-81">Li</span>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors" data-id="element-82">
                  <span className="text-xs font-bold" data-id="element-83">Tw</span>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors" data-id="element-84">
                  <span className="text-xs font-bold" data-id="element-85">Fb</span>
                </a>
              </div>
            </div>

            <div data-id="element-86">
              <h3 className="text-white font-display font-bold mb-6 text-lg" data-id="element-87">
                Training
              </h3>
              <ul className="space-y-3 text-sm" data-id="element-88">
                <li data-id="element-89">
                  <Link to="/courses" className="text-forest-200 hover:text-ember-400 transition-colors flex items-center group" data-id="element-90">
                    <ChevronRight className="w-3 h-3 mr-1 group-hover:translate-x-1 transition-transform" data-id="element-91" />
                    HACCP Levels 1-3
                  </Link>
                </li>
                <li data-id="element-92">
                  <Link to="/courses" className="text-forest-200 hover:text-ember-400 transition-colors flex items-center group" data-id="element-93">
                    <ChevronRight className="w-3 h-3 mr-1 group-hover:translate-x-1 transition-transform" data-id="element-94" />
                    Allergen Awareness
                  </Link>
                </li>
                <li data-id="element-95">
                  <Link to="/courses" className="text-forest-200 hover:text-ember-400 transition-colors flex items-center group" data-id="element-96">
                    <ChevronRight className="w-3 h-3 mr-1 group-hover:translate-x-1 transition-transform" data-id="element-97" />
                    Food Safety Management
                  </Link>
                </li>
                <li data-id="element-98">
                  <Link to="/corporate" className="text-forest-200 hover:text-ember-400 transition-colors flex items-center group" data-id="element-99">
                    <ChevronRight className="w-3 h-3 mr-1 group-hover:translate-x-1 transition-transform" data-id="element-100" />
                    Corporate Solutions
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-101">
              <h3 className="text-white font-display font-bold mb-6 text-lg" data-id="element-102">
                Company
              </h3>
              <ul className="space-y-3 text-sm" data-id="element-103">
                <li data-id="element-104">
                  <Link to="/corporate" className="text-forest-200 hover:text-ember-400 transition-colors flex items-center group" data-id="element-105">
                    <ChevronRight className="w-3 h-3 mr-1 group-hover:translate-x-1 transition-transform" data-id="element-106" />
                    Corporate Services
                  </Link>
                </li>
                <li data-id="element-107">
                  <Link to="/resources" className="text-forest-200 hover:text-ember-400 transition-colors flex items-center group" data-id="element-108">
                    <ChevronRight className="w-3 h-3 mr-1 group-hover:translate-x-1 transition-transform" data-id="element-109" />
                    Resources & Templates
                  </Link>
                </li>
                <li data-id="element-110">
                  <Link to="/help" className="text-forest-200 hover:text-ember-400 transition-colors flex items-center group" data-id="element-111">
                    <ChevronRight className="w-3 h-3 mr-1 group-hover:translate-x-1 transition-transform" data-id="element-112" />
                    Help Centre
                  </Link>
                </li>
                <li data-id="element-113">
                  <Link to="/contact" className="text-forest-200 hover:text-ember-400 transition-colors flex items-center group" data-id="element-114">
                    <ChevronRight className="w-3 h-3 mr-1 group-hover:translate-x-1 transition-transform" data-id="element-115" />
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-116">
              <h3 className="text-white font-display font-bold mb-6 text-lg" data-id="element-117">
                Get in Touch
              </h3>
              <ul className="space-y-4 text-sm" data-id="element-118">
                <li className="flex items-start" data-id="element-119">
                  <MapPin className="w-4 h-4 mr-3 mt-0.5 text-ember-400 flex-shrink-0" data-id="element-120" />
                  <span className="text-forest-200" data-id="element-121">
                    Unit 4, Park West Business Park,
                    <br data-id="element-122" />
                    Dublin 12, Ireland
                  </span>
                </li>
                <li className="flex items-center" data-id="element-123">
                  <Phone className="w-4 h-4 mr-3 text-ember-400 flex-shrink-0" data-id="element-124" />
                  <a href="tel:014567890" className="text-forest-200 hover:text-ember-400 transition-colors" data-id="element-125">
                    01 456 7890
                  </a>
                </li>
                <li className="flex items-center" data-id="element-126">
                  <Mail className="w-4 h-4 mr-3 text-ember-400 flex-shrink-0" data-id="element-127" />
                  <a href="mailto:info@foodsafeireland.ie" className="text-forest-200 hover:text-ember-400 transition-colors" data-id="element-128">
                    info@foodsafeireland.ie
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-forest-300" data-id="element-129">
            <p data-id="element-130">
              &copy; {new Date().getFullYear()} FoodSafe Ireland. All rights
              reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0" data-id="element-131">
              <Link to="/privacy" className="hover:text-ember-400 transition-colors" data-id="element-132">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-ember-400 transition-colors" data-id="element-133">
                Terms of Service
              </Link>
              <Link to="/cookies" className="hover:text-ember-400 transition-colors" data-id="element-134">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}